import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import '../styles/publicSite.css';

const asset = (name) => `/landing/files/${name}`;

function PublicSiteShell({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const navLinkClass = ({ isActive }) => (isActive ? 'active' : undefined);

  return (
    <div className="public-site">
      <header className="public-header">
        <nav className="public-nav" aria-label="Public navigation">
          <Link to="/" className="public-logo" aria-label="Nova homepage">
            <span className="public-logo-mark">N</span>
            <span>NOVA</span>
          </Link>

          <button
            type="button"
            className={`public-menu-button ${menuOpen ? 'active' : ''}`}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>

          <div className={`public-nav-links ${menuOpen ? 'active' : ''}`}>
            <NavLink to="/" end className={navLinkClass}>Features</NavLink>
            <NavLink to="/about" className={navLinkClass}>About</NavLink>
            <NavLink to="/contacts" className={navLinkClass}>Contact</NavLink>
            <Link to="/login" className="public-login-link">Log in</Link>
            <Link to="/register" className="public-nav-cta">Sign up</Link>
          </div>
        </nav>
      </header>

      {children}

      <footer className="public-footer">
        <div className="public-footer-inner">
          <span>&copy; NOVA, {new Date().getFullYear()}. All rights reserved.</span>
          <div className="public-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/policy">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LandingHome() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0 });
      return;
    }

    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <PublicSiteShell>
      <main>
        <section className="public-hero">
          <div className="public-hero-inner">
            <h1>Nova</h1>
            <p className="public-hero-copy">
              The ultimate data-driven football management simulator. Play against your friends online, anywhere, on any device.
            </p>
            <div className="public-hero-actions">
              <Link to="/play" className="public-button primary">Play now — no signup</Link>
              <Link to="/play-with-friends" className="public-button secondary">Play with friends</Link>
              <a href="#learn-more" className="public-button secondary">Learn more</a>
            </div>
          </div>
        </section>

        <section className="public-section public-feature-list" id="learn-more">
          <PublicFeature
            image="info_1.png"
            title="Powered by real world stats"
            text="Nova uses real player data to shape player attributes and form. Once your season starts, it is up to you to improve your squad and make the right football decisions."
          />
          <PublicFeature
            image="info_2.png"
            title="Build your dream team"
            text="Make offers, buy from the talent pool, negotiate with other managers, and tune your formation and tactics around the players you believe in."
            reverse
          />
          <PublicFeature
            image="info_3.png"
            title="Compete against your friends"
            text="Create leagues with friends or join other Nova players. Follow live matches, adjust your team, and fight for the table over a full season."
          />
        </section>

        <section className="public-manager-band">
          <div className="public-manager-image">
            <img src={asset('manager.png')} alt="Nova manager preview" />
          </div>
          <div className="public-manager-copy">
            <p className="public-kicker">Coming soon</p>
            <h2>Become the ultimate manager</h2>
            <p>
              Battle across seasons, tweak your squad, and climb leaderboards as Nova grows with more ways to play and compete.
            </p>
          </div>
        </section>

        <section className="public-section public-feature-list compact">
          <PublicFeature
            image="info_4_1.png"
            title="Flexible playing modes"
            text="Create your own league, invite friends, or join existing leagues. Build multiple teams and compete in different environments at the same time."
          />
          <PublicFeature
            image="info_4_2.png"
            title="Track your progress"
            text="A future dashboard will help you follow teams, leagues, players, achievements, and community status from one place."
            reverse
          />
        </section>

        <section className="public-coming">
          <div>
            <p className="public-kicker">Roadmap</p>
            <h2>Coming soon</h2>
            <p>
              We are working on more features after launch and plan to keep improving the game every day.
            </p>
          </div>
          <ul>
            <li>Squad training</li>
            <li>Youth teams</li>
            <li>Sponsorships</li>
            <li>Rewards for achievements</li>
            <li>Monetization for users</li>
            <li>Custom messenger</li>
          </ul>
        </section>

        <section className="public-community">
          <div>
            <p className="public-kicker">Community</p>
            <h2>Join the community</h2>
            <p>Stay up to date with the project by joining our social networks and online communities.</p>
          </div>
          <div className="public-socials">
            <a href="https://t.me/+gs_QwZDG5FJhOGVi" target="_blank" rel="noreferrer" aria-label="Telegram">
              <img src={asset('tele.svg')} alt="" />
            </a>
            <a href="https://discord.gg/ZSZTheEk" target="_blank" rel="noreferrer" aria-label="Discord">
              <img src={asset('soc_1.png')} alt="" />
            </a>
            <a href="https://www.instagram.com/novagame.io/" target="_blank" rel="noreferrer" aria-label="Instagram">
              <img src={asset('soc_2.png')} alt="" />
            </a>
            <a href="https://x.com/novagame_io" target="_blank" rel="noreferrer" aria-label="X">
              <img src={asset('soc_5.svg')} alt="" />
            </a>
          </div>
        </section>
      </main>
    </PublicSiteShell>
  );
}

function PublicFeature({ image, title, text, reverse = false }) {
  return (
    <article className={`public-feature ${reverse ? 'reverse' : ''}`}>
      <div className="public-feature-image">
        <img src={asset(image)} alt={title} />
      </div>
      <div className="public-feature-copy">
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
    </article>
  );
}

function LandingAbout() {
  return (
    <PublicSiteShell>
      <main className="public-page">
        <div className="public-page-heading">
          <p className="public-kicker">About Nova</p>
          <h1>About</h1>
        </div>

        <section className="public-section public-feature-list">
          <PublicFeature
            image="about_1.png"
            title="Fantasy football accessibility with manager-style depth"
            text="Nova is an online fantasy football simulator that sits between classic fantasy football and deeper football manager games. It runs on a text-based match engine and draws player attributes from real world data."
          />
          <PublicFeature
            image="about_2.png"
            title="Gameplay"
            text="Build a squad, choose formations, set tactics, trade with other users, and manage live matches with substitutions and tactical changes."
            reverse
          />
        </section>

        <section className="public-bullet-band">
          <h2>What is included</h2>
          <ul>
            <li>Player database from major European leagues</li>
            <li>Player attributes based on football data</li>
            <li>On-pitch formations and tactical choices</li>
            <li>Transfers between users and the talent pool</li>
            <li>Real-time PvP matches</li>
            <li>Cards, injuries, substitutions, and tactical changes</li>
          </ul>
        </section>
      </main>
    </PublicSiteShell>
  );
}

function LandingContacts() {
  return (
    <PublicSiteShell>
      <main className="public-page">
        <div className="public-page-heading">
          <p className="public-kicker">Talk to us</p>
          <h1>Contact</h1>
        </div>

        <section className="public-contact-grid">
          <div className="public-contact-item">
            <h2>General enquiries</h2>
            <a href="mailto:novasoccer26@gmail.com">novasoccer26@gmail.com</a>
          </div>
          <div className="public-contact-item">
            <h2>Business enquiries</h2>
            <a href="mailto:filip@otlr.digital">filip@otlr.digital</a>
          </div>
          <div className="public-contact-item">
            <h2>Community</h2>
            <a href="https://discord.gg/ZSZTheEk" target="_blank" rel="noreferrer">
              <img src={asset('soc_1.png')} alt="" />
              Nova Soccer
            </a>
          </div>
        </section>
      </main>
    </PublicSiteShell>
  );
}

function LandingPolicy() {
  return (
    <PublicSiteShell>
      <main className="public-page public-policy">
        <div className="public-page-heading">
          <p className="public-kicker">Privacy</p>
          <h1>Privacy Policy</h1>
          <p>Effective Date: 27.07.2026</p>
        </div>

        <section>
          <h2>Who We Are</h2>
          <p>DIGITALNA RESENJA DOO ("Nova", "we", "us", or "our") is the data controller of personal data processed when you use Nova, our online football manager simulation game, or visit our website.</p>
          <p>Email: <a href="mailto:novasoccer26@gmail.com">novasoccer26@gmail.com</a></p>
          <p>Address: 11000 BEOGRAD, KRALJICE NATALIJE 11</p>
        </section>

        <section>
          <h2>What Data We Collect</h2>
          <ul>
            <li>Account data: email address, username, first and last name, date of birth, country, and an avatar image if you upload one. Your password is handled by our authentication provider and is never visible to us.</li>
            <li>Social sign-in data: if you sign in with Google or X, we receive basic profile details such as your name and email address from that provider.</li>
            <li>Guest play: you can try Nova without an account. We then create a temporary guest profile tied to your browser. If you sign up later, your guest progress is linked to your new account.</li>
            <li>Game data: your teams, squads, tactics, transfers, league memberships, match results, and match history.</li>
            <li>Invitation data: email addresses you enter to invite friends to a league. We use them only to send and manage that invitation.</li>
            <li>Technical data: IP address, device and browser information, and cookies needed to keep you logged in.</li>
            <li>Usage data: how you use Nova, such as pages visited and features used, so we can improve the game.</li>
          </ul>
        </section>

        <section>
          <h2>Purpose and Legal Basis for Processing</h2>
          <ul>
            <li>To provide Nova, run your account, and save your game progress. Legal basis: performance of a contract.</li>
            <li>To send league invitations you ask us to send. Legal basis: performance of a contract and our legitimate interest in operating invitation features.</li>
            <li>To keep Nova secure, prevent cheating and abuse, and fix problems. Legal basis: legitimate interests.</li>
            <li>To understand how Nova is used and improve it. Legal basis: legitimate interests.</li>
            <li>To send product updates or news by email. Legal basis: consent, which you can withdraw at any time by contacting us or using the unsubscribe link in any email.</li>
          </ul>
        </section>

        <section>
          <h2>Data Storage and Retention</h2>
          <p>We store your personal data securely and keep it while your account is active. If you delete your account, or ask us to, we delete or anonymise your personal data unless the law requires us to keep it longer.</p>
          <p>Guest profiles and their game data are automatically deleted after a period of inactivity. Invitation emails are kept only as long as needed to manage the invitation.</p>
        </section>

        <section>
          <h2>Data Sharing and Transfers</h2>
          <p>We use third-party service providers to run Nova, such as authentication, database hosting, file storage, email delivery, analytics, and content delivery and security services. These providers process your data on our behalf and under data protection agreements. We do not sell your personal data.</p>
          <p>If we transfer your data outside the European Economic Area, we use appropriate safeguards such as Standard Contractual Clauses where applicable.</p>
        </section>

        <section>
          <h2>Your Rights Under GDPR</h2>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Request correction or deletion of your data</li>
            <li>Restrict or object to processing</li>
            <li>Withdraw your consent at any time</li>
            <li>Receive a copy of your data in a portable format</li>
            <li>Lodge a complaint with a data protection authority</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:novasoccer26@gmail.com">novasoccer26@gmail.com</a>.</p>
        </section>

        <section>
          <h2>Updates to This Privacy Policy</h2>
          <p>We may update this Privacy Policy to reflect changes in our practices or legal obligations. We will post the updated version here with a new effective date.</p>
        </section>
      </main>
    </PublicSiteShell>
  );
}

function LandingTerms() {
  return (
    <PublicSiteShell>
      <main className="public-page public-policy">
        <div className="public-page-heading">
          <p className="public-kicker">Legal</p>
          <h1>Terms of Service</h1>
          <p>Effective Date: 01.07.2026</p>
        </div>

        <section>
          <h2>Who We Are</h2>
          <p>DIGITALNA RESENJA DOO ("Nova", "we", "us", or "our") operates Nova, an online football manager simulation game.</p>
          <p>Address: 11000 BEOGRAD, KRALJICE NATALIJE 11</p>
          <p>Contact: <a href="mailto:novasoccer26@gmail.com">novasoccer26@gmail.com</a></p>
        </section>

        <section>
          <h2>Accepting These Terms</h2>
          <p>By accessing or using Nova, you agree to these Terms. If you do not agree, do not use Nova.</p>
          <p>If you use Nova for someone else, such as a company, group, or league, you confirm that you have permission to accept these Terms for them.</p>
        </section>

        <section>
          <h2>Who Can Use Nova</h2>
          <p>Nova is not intended for children under 13. If you are under the age of majority where you live, you may use Nova only with permission from a parent or legal guardian.</p>
          <p>You must not use Nova if the law in your country or region does not allow you to use online games or services like Nova.</p>
        </section>

        <section>
          <h2>Your Account</h2>
          <p>You may need an account to use Nova. You are responsible for keeping your login details safe and for activity that happens through your account.</p>
          <p>Please give accurate account information, including your email address, and keep it up to date. Tell us quickly if you think someone else has accessed your account.</p>
        </section>

        <section>
          <h2>What Nova Provides</h2>
          <p>Nova lets users create teams, join leagues, invite other managers, manage squads, choose tactics, make transfers, follow fixtures, and play simulated football matches.</p>
          <p>Nova uses game logic and football data to create player attributes, match events, commentary, standings, and other results. These outputs are for entertainment and may not match real-world football results or player performance.</p>
        </section>

        <section>
          <h2>Virtual Game Items</h2>
          <p>Players, squads, coins, budgets, bids, transfers, boosts, rewards, league standings, match results, and other in-game items are virtual only.</p>
          <p>They do not have cash value, cannot be sold outside Nova, cannot be exchanged for money, and do not give you ownership of real players, clubs, leagues, or data.</p>
        </section>

        <section>
          <h2>No Gambling</h2>
          <p>Nova is a football manager simulation game. It is not a betting, wagering, casino, lottery, or gambling service.</p>
          <p>You must not use Nova to run bets, wagers, cash prizes, illegal contests, or real-money trading. We may suspend or remove accounts, leagues, teams, or content that appear to be used for those purposes.</p>
        </section>

        <section>
          <h2>Your Content</h2>
          <p>You may add content to Nova, such as usernames, team names, league names, avatars, logos, league images, lineup choices, tactics, invitations, and messages if messaging features are available.</p>
          <p>You keep the rights you already have in your content. You give us permission to host, store, copy, display, resize, and use that content as needed to operate, protect, and improve Nova.</p>
          <p>Do not upload or submit content that is illegal, abusive, hateful, misleading, spam, malware, sexually explicit, invasive of privacy, or that infringes someone else's rights.</p>
        </section>

        <section>
          <h2>Fair Play Rules</h2>
          <p>You must not cheat, exploit bugs, abuse multiple accounts, harass other users, impersonate others, scrape Nova, disrupt the service, attack our systems, bypass security, or use bots or scripts to gain an unfair advantage.</p>
          <p>If you find a bug or exploit, please report it instead of using it. We may reverse game actions, cancel transfers, adjust standings, remove content, suspend accounts, or take other action to protect the game.</p>
        </section>

        <section>
          <h2>Leagues And League Owners</h2>
          <p>League owners can manage league settings, invitations, membership, fixture generation, and some league rules inside Nova.</p>
          <p>League owners must use those tools fairly and must not use them to harass users, avoid these Terms, or run illegal activity. Nova may step in if a league affects safety, security, or game integrity.</p>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <p>Nova may use third-party services for authentication, storage, email, push notifications, analytics, ads, hosting, databases, and other operations.</p>
          <p>Your use of those features may also be affected by the third party's own terms and policies. Our Privacy Policy explains more about personal data.</p>
        </section>

        <section>
          <h2>Changes And Availability</h2>
          <p>Nova is still growing. We may add, remove, reset, rebalance, limit, or change features, player data, game rules, virtual items, match behavior, or access to the service.</p>
          <p>We try to keep Nova available, but we do not promise that it will always be online, error-free, secure, or unchanged.</p>
        </section>

        <section>
          <h2>Suspension And Ending Access</h2>
          <p>We may suspend or end access to Nova if we reasonably believe you broke these Terms, created risk for Nova or other users, or used the service in a harmful or illegal way.</p>
          <p>You can stop using Nova at any time. To ask about account deletion or data rights, contact us using the email above or the contact details in our Privacy Policy.</p>
        </section>

        <section>
          <h2>Ownership Of Nova</h2>
          <p>Nova, including its software, design, text, graphics, logos, game systems, and original content, belongs to us or our licensors. You may use it only as allowed by these Terms.</p>
          <p>Real-world football names, clubs, leagues, competitions, players, data, and trademarks belong to their respective owners. Nova is not affiliated with them unless we clearly say otherwise.</p>
        </section>

        <section>
          <h2>Disclaimers</h2>
          <p>To the fullest extent allowed by law, Nova is provided "as is" and "as available". We do not guarantee any specific match result, ranking, transfer outcome, player value, data accuracy, reward, or service availability.</p>
          <p>Nothing in these Terms removes legal rights that cannot be removed under applicable law.</p>
        </section>

        <section>
          <h2>Limits On Liability</h2>
          <p>To the fullest extent allowed by law, we are not responsible for indirect, incidental, special, consequential, or punitive losses, including lost profits, lost data, lost goodwill, or loss of virtual items.</p>
          <p>If we are liable to you for any reason, our total liability will be limited to the greater of the amount you paid Nova in the six months before the claim or EUR 100, unless applicable law requires a higher amount.</p>
        </section>

        <section>
          <h2>Governing Law</h2>
          <p>Unless your local consumer law says otherwise, these Terms are governed by the laws of Serbia.</p>
        </section>

        <section>
          <h2>Changes To These Terms</h2>
          <p>We may update these Terms from time to time. If a change is important, we will take reasonable steps to tell you, such as posting the updated Terms on Nova.</p>
          <p>Your continued use of Nova after the updated Terms take effect means you accept the updated Terms.</p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>If you have questions about these Terms, contact us at <a href="mailto:novasoccer26@gmail.com">novasoccer26@gmail.com</a>.</p>
        </section>
      </main>
    </PublicSiteShell>
  );
}

export { LandingHome, LandingAbout, LandingContacts, LandingPolicy, LandingTerms, PublicSiteShell };
